// Folder-upload engine for the File Library.
//
// Supports uploading a whole folder tree (files + nested sub-folders), with a live progress bar and
// crash-resume: the job (which files are done) is persisted to IndexedDB, so after a reload / power
// cut / shutdown the upload can continue instead of starting over.
//
// Two folder-picking modes:
//   • File System Access API (Chrome/Edge) — the directory HANDLE is stored in IndexedDB, so on the
//     next visit we re-acquire it (one permission click) and auto-resume, no re-selecting.
//   • <input webkitdirectory> fallback (Safari/Firefox) — resume needs the user to re-pick the folder;
//     already-uploaded files are then skipped (the server upload is idempotent on name+size).
//
// The server side is idempotent: POST /files/upload skips a file already present (same folder + name +
// size), and POST /files/ensure-path reuses existing folders — so re-running a job never duplicates.

import { api } from '../../utils/api';

const MAX_BYTES = 200 * 1024 * 1024;   // matches the server's per-file cap
const CONCURRENCY = 4;

export interface UploadEntry { relPath: string; name: string; size: number; file?: File; handle?: any; }
export interface UploadJob {
  id: string;
  scope: string;            // 'personal' | 'client:<id>'
  scopeLabel: string;       // human label for the resume banner
  rootParentId: string;     // file_nodes parent the picked folder is uploaded under
  rootName: string;         // picked folder name (display + resume sanity-check)
  total: number;
  done: string[];           // relPaths fully uploaded (persisted for resume)
  failed: { relPath: string; reason: string }[];
  dirHandle: any | null;    // FileSystemDirectoryHandle when the FS Access API was used (persistable)
  createdAt: number;
}

export const hasFsAccess = (): boolean => typeof (window as any).showDirectoryPicker === 'function';

// ── IndexedDB (stores jobs incl. the directory handle) ───────────────────────
const DB_NAME = 'slf-file-uploads';
const STORE = 'jobs';
function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(DB_NAME, 1);
    r.onupgradeneeded = () => { if (!r.result.objectStoreNames.contains(STORE)) r.result.createObjectStore(STORE, { keyPath: 'id' }); };
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}
export async function saveJob(job: UploadJob): Promise<void> {
  const db = await openDb();
  return new Promise((res, rej) => { const tx = db.transaction(STORE, 'readwrite'); tx.objectStore(STORE).put(job); tx.oncomplete = () => res(); tx.onerror = () => rej(tx.error); });
}
export async function allJobs(): Promise<UploadJob[]> {
  const db = await openDb();
  return new Promise((res, rej) => { const tx = db.transaction(STORE, 'readonly'); const rq = tx.objectStore(STORE).getAll(); rq.onsuccess = () => res((rq.result || []) as UploadJob[]); rq.onerror = () => rej(rq.error); });
}
export async function deleteJob(id: string): Promise<void> {
  const db = await openDb();
  return new Promise((res, rej) => { const tx = db.transaction(STORE, 'readwrite'); tx.objectStore(STORE).delete(id); tx.oncomplete = () => res(); tx.onerror = () => rej(tx.error); });
}
// Incomplete jobs (something left to upload) — drives the resume banner.
export async function pendingJobs(): Promise<UploadJob[]> {
  return (await allJobs()).filter(j => (j.done?.length || 0) < j.total);
}

// ── Enumeration ──────────────────────────────────────────────────────────────
// Skip regenerable dev / OS junk, so uploading a code project (or any Mac folder) doesn't drag in tens
// of thousands of node_modules / .git files. Matched on the folder/file name (any path segment).
const IGNORE_DIRS = new Set(['node_modules', '.git', '.svn', '.hg', '.cache', '__pycache__', '.venv', 'venv', '.idea', '.vscode', '.pnpm-store', '.gradle', '.terraform', '.next', '.nuxt', '.turbo', '.parcel-cache']);
const IGNORE_FILES = new Set(['.DS_Store', 'Thumbs.db', 'desktop.ini']);
// macOS AppleDouble resource-fork shadows ('._foo.pdf') are 4 KB of metadata, not documents —
// 91 of them reached the archive before this filter existed.
export const isJunkFile = (name: string) => IGNORE_FILES.has(name) || name.startsWith('._');
export const isIgnoredPath = (relPath: string, name: string): boolean =>
  isJunkFile(name) || relPath.split('/').some(seg => IGNORE_DIRS.has(seg));

// FS Access API: walk the directory handle. relPath includes the root folder name so the structure
// (incl. the picked folder itself) is recreated under the current location.
export async function enumerateHandle(dir: any, prefix: string, out: UploadEntry[] = []): Promise<UploadEntry[]> {
  const it = dir.values();
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const next = await it.next();
    if (next.done) break;
    const h = next.value;
    const rel = prefix ? `${prefix}/${h.name}` : h.name;
    if (h.kind === 'file') { if (!isJunkFile(h.name)) out.push({ relPath: rel, name: h.name, size: 0, handle: h }); }
    else if (h.kind === 'directory') { if (!IGNORE_DIRS.has(h.name)) await enumerateHandle(h, rel, out); }
  }
  return out;
}
// webkitdirectory FileList: webkitRelativePath already carries the root folder name + sub-path.
export function enumerateFileList(files: FileList | File[]): UploadEntry[] {
  return Array.from(files)
    .map(f => ({ relPath: (f as any).webkitRelativePath || f.name, name: f.name, size: f.size, file: f }))
    .filter(e => !isIgnoredPath(e.relPath, e.name));
}

// Drag-drop: traverse dropped items via webkitGetAsEntry so dropped FOLDERS recurse (not just files).
// IMPORTANT: webkitGetAsEntry() must be called synchronously in the drop handler, so the caller passes
// the already-collected entry list (see grabEntries) — this walks them asynchronously.
function readDir(reader: any): Promise<any[]> { return new Promise(res => reader.readEntries((r: any[]) => res(r || []), () => res([]))); }
async function walkEntry(entry: any, prefix: string, out: UploadEntry[]): Promise<void> {
  const rel = prefix ? `${prefix}/${entry.name}` : entry.name;
  if (entry.isFile) {
    if (isJunkFile(entry.name)) return;
    const file: File = await new Promise((res, rej) => entry.file(res, rej));
    out.push({ relPath: rel, name: entry.name, size: file.size, file });
  } else if (entry.isDirectory) {
    if (IGNORE_DIRS.has(entry.name)) return;
    const reader = entry.createReader();
    let batch: any[]; do { batch = await readDir(reader); for (const e of batch) await walkEntry(e, rel, out); } while (batch.length);
  }
}
// Synchronously pull the FileSystemEntry objects out of a drop event's items (call inside onDrop).
export function grabEntries(dt: DataTransfer): any[] {
  const entries: any[] = [];
  const items = dt.items;
  for (let i = 0; i < items.length; i++) { const it = items[i]; if (it.kind !== 'file') continue; const e = (it as any).webkitGetAsEntry?.(); if (e) entries.push(e); }
  return entries;
}
export async function enumerateEntries(entries: any[]): Promise<UploadEntry[]> {
  const out: UploadEntry[] = [];
  for (const e of entries) await walkEntry(e, '', out);
  return out;
}

// ── Deadlines: nothing in this engine may hang forever ─────────────────────────────────────────
// fetch() has no timeout and neither does FileSystemFileHandle.getFile(). On Windows the second one
// is the dangerous one: a OneDrive / cloud "placeholder" file has no local bytes, so getFile()
// triggers a hydration download that can stall indefinitely. With four worker slots, four stalls
// froze the whole job — no error, no progress, server idle and answering 201 to everything it did
// receive. The owner hit exactly this at 480/3200 files (2026-08-24). Every await below now has a
// deadline, so a stuck file becomes a normal failure the pool can step over.
const GETFILE_MS = 60_000;                                  // reading one file off disk/cloud
const ENSURE_MS = 60_000;                                   // creating one folder
// Uploading scales with size: 2 min of grace plus 10 s per MB, capped at 10 min. A document that
// cannot move 1 MB in 10 s is not going to finish; it is recorded and retried on the next pass.
const uploadMs = (bytes: number) => Math.min(10 * 60_000, 120_000 + (bytes / (1024 * 1024)) * 10_000);

class TimeoutError extends Error {
  constructor(what: string, ms: number) { super(`${what} timed out after ${Math.round(ms / 1000)}s`); this.name = 'TimeoutError'; }
}
/** Reject after `ms` — for promises that cannot be aborted (getFile). */
function deadline<T>(p: Promise<T>, ms: number, what: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(() => reject(new TimeoutError(what, ms)), ms);
    p.then(v => { clearTimeout(id); resolve(v); }, e => { clearTimeout(id); reject(e); });
  });
}
/** Same, but actually ABORTS the request so a dead socket is released instead of leaking. */
async function withAbort<T>(ms: number, what: string, fn: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const ac = new AbortController();
  const id = setTimeout(() => ac.abort(), ms);
  try { return await fn(ac.signal); }
  catch (e: any) { throw ac.signal.aborted ? new TimeoutError(what, ms) : e; }
  finally { clearTimeout(id); }
}

// Retry transient failures (server restart / 502-503-504 / network blip) with a short backoff, so a
// brief hiccup mid-upload self-heals instead of aborting the job. Permanent errors (e.g. 400) throw
// immediately — no point retrying. Backoff totals ~10s, longer than a normal server restart.
const TRANSIENT = /bad gateway|gateway tim|temporarily|50[234]|failed to fetch|networkerror|network error|load failed|timed? *out|abort|ECONN|socket hang/i;
// How many whole passes a file gets before the job gives up on it and moves on. Without a ceiling a
// permanently unreadable file would keep the job alive forever.
const MAX_ATTEMPTS = 3;
async function withRetry<T>(fn: () => Promise<T>, tries = 5): Promise<T> {
  let lastErr: any;
  for (let i = 0; i < tries; i++) {
    try { return await fn(); }
    catch (e: any) {
      lastErr = e;
      if (i < tries - 1 && TRANSIENT.test(String(e?.message || ''))) await new Promise(r => setTimeout(r, Math.min(1000 * (i + 1), 5000)));
      else break;
    }
  }
  throw lastErr;
}

// ── Run / resume a job ─────────────────────────────────────────────────────────
// Runs PASSES until nothing is left or every straggler has used up its attempts. A pass that ends
// with files still pending (all of them timed out or hit a transient error) is followed by another
// one automatically — the operator never has to notice, let alone press anything.
export async function runUpload(
  job: UploadJob,
  entries: UploadEntry[],
  cb: { onProgress: (done: number, total: number, current: string) => void; isCancelled: () => boolean },
): Promise<UploadJob> {
  const doneSet = new Set(job.done || []);
  const attempts = new Map<string, number>();
  cb.onProgress(doneSet.size, job.total, '');

  for (let pass = 1; ; pass++) {
    if (cb.isCancelled()) break;
    const pending = entries.filter(e => !doneSet.has(e.relPath) && (attempts.get(e.relPath) || 0) < MAX_ATTEMPTS);
    if (!pending.length) break;
    const label = pass > 1 ? ` (retry ${pass - 1})` : '';

    // Phase 1 — create every needed folder path up front, sequentially (so concurrent uploads never race
    // to create the same ancestor folder). Cache dir-relpath → folder id.
    const pathCache = new Map<string, string>([['', job.rootParentId]]);
    const fileDirs = Array.from(new Set(pending.map(e => e.relPath.split('/').slice(0, -1).join('/'))));
    fileDirs.sort((a, b) => a.split('/').length - b.split('/').length || a.localeCompare(b));
    let madeDirs = 0;
    for (const d of fileDirs) {
      if (cb.isCancelled()) return job;
      cb.onProgress(doneSet.size, job.total, `Preparing folders${label}… (${madeDirs}/${fileDirs.length})`);
      if (pathCache.has(d)) { madeDirs++; continue; }
      try {
        const r: any = await withRetry(() => withAbort(ENSURE_MS, `Creating folder "${d.split('/').pop()}"`,
          sig => api.filesEnsurePath(job.scope, job.rootParentId, d ? d.split('/') : [], sig)));
        pathCache.set(d, r.id);
      } catch (e: any) {
        // One unreachable folder must not sink the whole tree — its files are recorded and the rest
        // of the upload carries on (before this, the throw aborted every remaining file).
        for (const en of pending) {
          if (en.relPath.split('/').slice(0, -1).join('/') !== d) continue;
          job.failed.push({ relPath: en.relPath, reason: `folder failed: ${String(e?.message || e).slice(0, 160)}` });
          doneSet.add(en.relPath);
        }
        job.done = Array.from(doneSet);
      }
      madeDirs++;
    }

    // Phase 2 — upload pending files with a small concurrency pool.
    const queue = pending.filter(e => !doneSet.has(e.relPath));
    let idx = 0, active = 0, sincePersist = 0;
    await new Promise<void>((resolve) => {
      if (!queue.length) return resolve();
      const persist = () => { if (++sincePersist >= 8) { sincePersist = 0; saveJob(job); } };
      const pump = () => {
        if (cb.isCancelled()) { if (active === 0) resolve(); return; }
        while (active < CONCURRENCY && idx < queue.length) {
          const e = queue[idx++]; active++;
          (async () => {
            attempts.set(e.relPath, (attempts.get(e.relPath) || 0) + 1);
            try {
              const folderId = pathCache.get(e.relPath.split('/').slice(0, -1).join('/')) || job.rootParentId;
              // getFile() is the cloud-placeholder trap — give it its own deadline.
              const file: File = e.file || await deadline(e.handle.getFile(), GETFILE_MS, `Reading "${e.name}"`);
              if (file.size > MAX_BYTES) { job.failed.push({ relPath: e.relPath, reason: 'exceeds 200 MB' }); }
              else { await withRetry(() => withAbort(uploadMs(file.size), `Uploading "${e.name}"`, sig => api.filesUpload(job.scope, folderId, file, sig))); }
              doneSet.add(e.relPath); job.done = Array.from(doneSet);
            } catch (err: any) {
              const msg = String(err?.message || err || '');
              const usedUp = (attempts.get(e.relPath) || 0) >= MAX_ATTEMPTS;
              if (TRANSIENT.test(msg) && !usedUp) {
                // Transient (stall / server restart / network) — left pending; the NEXT pass retries it.
              } else {
                // Permanent, or out of attempts: record it and COUNT it, so the job can finish and
                // surface the reason instead of sitting stuck at "480/3200" forever.
                job.failed.push({ relPath: e.relPath, reason: msg.slice(0, 200) || 'upload failed' });
                doneSet.add(e.relPath); job.done = Array.from(doneSet);
              }
            } finally {
              active--; cb.onProgress(doneSet.size, job.total, e.name + label); persist();
              if (idx >= queue.length && active === 0) resolve(); else pump();
            }
          })();
        }
      };
      pump();
    });
    await saveJob(job);
    // Let a flaky network settle before the next sweep.
    if (!cb.isCancelled() && entries.some(e => !doneSet.has(e.relPath))) await new Promise(r => setTimeout(r, 2000));
  }

  // Anything still not in `done` has exhausted its attempts — bank it as failed so the job is
  // genuinely finished and the resume banner does not reappear for files that will never load.
  for (const e of entries) {
    if (doneSet.has(e.relPath)) continue;
    if (cb.isCancelled()) break;
    job.failed.push({ relPath: e.relPath, reason: `gave up after ${MAX_ATTEMPTS} attempts` });
    doneSet.add(e.relPath);
  }
  job.done = Array.from(doneSet);
  await saveJob(job);
  return job;
}
